'use client';

import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import Header from '@/components/Header';
import HeroImage from '@/components/HeroImage';
import Footer from '@/components/Footer';
import { testimonials } from '@/lib/data';
import styles from './testimonials.module.css';
import Image from 'next/image';
import { BreadcrumbSchema, TestimonialsPageSchema } from '@/lib/schema';


// Star Rating Component
function StarRating({ rating }: { rating: number }) {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className={styles.starRating}>
      {[...Array(fullStars)].map((_, i) => (
        <span key={`full-${i}`} className={styles.star}>
          *
        </span>
      ))}
      {hasHalfStar && <span className={styles.star}>*</span>}
      {[...Array(emptyStars)].map((_, i) => (
        <span key={`empty-${i}`} className={styles.starEmpty}>
          *
        </span>
      ))}
      <span className={styles.ratingNumber}>{rating.toFixed(1)}</span>
    </div>
  );
}

export default function TestimonialsPage() {
  const t = useTranslations('testimonials');
  const tCommon = useTranslations('common');

  // Calculate average rating
  const averageRating =
    testimonials.reduce((acc, testimonial) => acc + testimonial.rating, 0) / testimonials.length;

  // Format testimonials for schema (use English text for SEO schema)
  const reviewsForSchema = testimonials.map((testimonial) => ({
    name: testimonial.name,
    location: testimonial.location,
    date: testimonial.date,
    rating: testimonial.rating,
    text: t(`items.${testimonial.translationKey}.text`),
    adventure: t(`items.${testimonial.translationKey}.adventure`),
  }));

  return (
    <div className={styles.pageWrapper}>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: '/' },
        { name: tCommon('testimonials'), href: '/testimonials/' },
      ]} />
      <TestimonialsPageSchema reviews={reviewsForSchema} />
      <Header />

      <HeroImage
        backgroundImage="https://images.unsplash.com/photo-1551632811-561732d1e306?w=1920&q=80"
        title={t('hero.title')}
        subtitle={t('hero.subtitle')}
        location={t('hero.location')}
      />

      <main>
        {/* Intro Section with Stats */}
        <section className={styles.introSection}>
          <div className={styles.container}>
            <motion.div
              className={styles.introContent}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h1>{t('intro.title')}</h1>
              <p>{t('intro.description')}</p>

              <div className={styles.statsGrid}>
                <div className={styles.statItem}>
                  <div className={styles.statNumber}>{averageRating.toFixed(1)}</div>
                  <div className={styles.statLabel}>{t('stats.averageRating')}</div>
                  <StarRating rating={averageRating} />
                </div>
                <div className={styles.statItem}>
                  <div className={styles.statNumber}>100%</div>
                  <div className={styles.statLabel}>{t('stats.satisfaction')}</div>
                </div>
                <div className={styles.statItem}>
                  <div className={styles.statNumber}>{testimonials.length}+</div>
                  <div className={styles.statLabel}>{t('stats.happyAdventurers')}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Testimonials Grid */}
        <section className={styles.testimonialsSection}>
          <div className={styles.container}>
            <div className={styles.testimonialsGrid}>
              {testimonials.map((testimonial, index) => (
                <motion.article
                  key={testimonial.id}
                  className={`${styles.testimonialCard} ${
                    testimonial.featured ? styles.featured : ''
                  }`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className={styles.cardHeader}>
                    <div className={styles.clientImage}>
                      <Image
                        src={testimonial.image}
                        alt={t('items.portraitAlt', { name: testimonial.name, location: testimonial.location })}
                        fill
                        sizes="80px"
                        style={{ objectFit: 'cover' }}
                      />
                    </div>
                    <div className={styles.clientInfo}>
                      <h3>{testimonial.name}</h3>
                      <div className={styles.location}>{testimonial.location}</div>
                    </div>
                  </div>

                  <StarRating rating={testimonial.rating} />

                  <div className={styles.adventureType}>{t(`items.${testimonial.translationKey}.adventure`)}</div>

                  <blockquote className={styles.testimonialText}>
                    &ldquo;{t(`items.${testimonial.translationKey}.text`)}&rdquo;
                  </blockquote>

                  <div className={styles.testimonialDate}>{testimonial.date}</div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <motion.section
          className={styles.ctaSection}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className={styles.container}>
            <div className={styles.ctaContent}>
              <h2>{t('cta.title')}</h2>
              <p>{t('cta.description')}</p>
              <Link href="/contact/" className={styles.ctaButton}>
                {t('cta.button')}
              </Link>
            </div>
          </div>
        </motion.section>
      </main>

      <Footer />
    </div>
  );
}
